// CreateFormModal — "Add new form": a Form name field, an optional Folder select
// and an info note → POST /forms/create-form. On success the list refetches and
// the app opens the new form in the builder. (Advance settings from the design
// are still deferred to a later chunk.)

import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Alert, Button, Field, Input, Modal, SelectDropdown } from '@/components/common';
import { useCreateFormMutation } from '@/api/endpoints/formsApi';
import { useGetFormFoldersQuery } from '@/api/endpoints/formFoldersApi';

interface CreateFormModalProps {
  open: boolean;
  onClose: () => void;
}

const CreateFormModal = ({ open, onClose }: CreateFormModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [createForm, { isLoading }] = useCreateFormMutation();
  const [name, setName] = useState('');
  // Owning folder — null = unassigned, which is also what the API wants. Never
  // preselected from the page's sidebar selection: the modal always opens
  // unassigned.
  const [folder, setFolder] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // This modal stays mounted with the page (only Modal unmounts), so the query
  // needs `skip`; it shares the cache entry the folders sidebar populates.
  const { data: folders = [] } = useGetFormFoldersQuery(undefined, { skip: !open });
  const folderOptions = useMemo(
    () => [
      { value: null, label: t('forms_create_folder_none'), icon: 'folder-open' },
      ...folders.map((f) => ({ value: f.id, label: f.title, icon: 'folder' })),
    ],
    [folders, t],
  );

  useEffect(() => {
    if (open) {
      setName('');
      setFolder(null);
      setSubmitted(false);
    }
  }, [open]);

  const nameMissing = !name.trim();

  const submit = async () => {
    setSubmitted(true);
    if (nameMissing) return;
    const title = name.trim();
    try {
      const res = await createForm({ title, folder }).unwrap();
      toast.success(t('forms_create_success'));
      onClose();
      // Open the new form in the builder; if the API returns no id, the list
      // refetch (cache invalidation) still surfaces the new form.
      if (res?.formId)
        navigate('/form-templates/builder', { state: { formId: res.formId, formTitle: title } });
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon='plus'
      title={t('forms_create_title')}
      subtitle={t('forms_create_subtitle')}
      size='md'
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={isLoading}>
            {t('button_cancel')}
          </Button>
          <Button iconStart='plus' onClick={submit} loading={isLoading}>
            {t('forms_create_submit')}
          </Button>
        </>
      }
    >
      <Field
        label={t('forms_create_name_label')}
        required
        error={submitted && nameMissing ? t('forms_create_name_required') : undefined}
      >
        <Input
          autoFocus
          value={name}
          placeholder={t('forms_create_name_ph')}
          error={submitted && nameMissing}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={onKeyDown}
        />
      </Field>

      <Field label={t('forms_create_folder')}>
        <SelectDropdown
          iconStart='folder'
          size='lg'
          portal
          flip
          aria-label={t('forms_create_folder')}
          value={folder}
          onChange={(next) => setFolder(next)}
          options={folderOptions}
        />
      </Field>

      <Alert variant='neutral' className='forms-create__note'>
        {t('forms_create_note')}
      </Alert>
    </Modal>
  );
};

export default CreateFormModal;
